import { Text, View, Heading, Card } from '@smarterservices/smarter-ui';
import useAppStore from '../../stores/app';
import { DeviceSelector } from './DeviceSelector';
import React from 'react';
import config from '../../config';

/**
 * AudioDeviceSettings - Inner component for device selection
 */
const AudioDeviceSettings = () => {
  const audioDevices = useAppStore((state) => state.audioDevices);
  const setAudioInputDevice = useAppStore((state) => state.setAudioInputDevice);
  const setAudioOutputDevice = useAppStore((state) => state.setAudioOutputDevice);

  const [inputSaved, setInputSaved] = React.useState(false);
  const [outputSaved, setOutputSaved] = React.useState(false);
  const inputTimerRef = React.useRef(null);
  const outputTimerRef = React.useRef(null);

  const handleAudioInputChange = React.useCallback((deviceId, deviceLabel) => {
    console.log('[RoomSettings] Audio input device changed:', deviceId, deviceLabel);
    setAudioInputDevice(deviceId, deviceLabel);

    // Show saved message
    setInputSaved(true);

    // Clear existing timer
    if (inputTimerRef.current) {
      clearTimeout(inputTimerRef.current);
    }

    // Hide message after 10 seconds
    inputTimerRef.current = setTimeout(() => {
      setInputSaved(false);
    }, 10000);
  }, [setAudioInputDevice]);

  const handleAudioOutputChange = React.useCallback((deviceId, deviceLabel) => {
    console.log('[RoomSettings] Audio output device changed:', deviceId, deviceLabel);
    setAudioOutputDevice(deviceId, deviceLabel);

    // Show saved message
    setOutputSaved(true);

    // Clear existing timer
    if (outputTimerRef.current) {
      clearTimeout(outputTimerRef.current);
    }

    // Hide message after 10 seconds
    outputTimerRef.current = setTimeout(() => {
      setOutputSaved(false);
    }, 10000);
  }, [setAudioOutputDevice]);

  // Cleanup timers on unmount
  React.useEffect(() => {
    return () => {
      if (inputTimerRef.current) clearTimeout(inputTimerRef.current);
      if (outputTimerRef.current) clearTimeout(outputTimerRef.current);
    };
  }, []);

  return (
    <>
      {/* Audio Input (Microphone) */}
      <Card margin="none none medium none">
        <Card.Header>
          <Card.Title>
            <Text weight="bold">Microphone Input</Text>
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Text size="small" margin="none none small none" padding="small small none small">
            Select the microphone you want to use for voice communication
          </Text>
          <DeviceSelector
            kind="audioinput"
            label="Microphone"
            initialDeviceLabel={audioDevices.inputDeviceLabel}
            onDeviceChange={handleAudioInputChange}
          />
          {inputSaved && (
            <View as="div" padding="none small">
              <Text size="legend" color="success">
                Device setting saved
              </Text>
            </View>
          )}
        </Card.Body>
      </Card>

      {/* Audio Output (Speakers) */}
      <Card margin="none none medium none">
        <Card.Header>
          <Card.Title>
            <Text weight="bold">Audio Output</Text>
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <Text size="small" margin="none none small none" padding="small small none small">
            Select the speakers or headphones for audio playback
          </Text>
          <DeviceSelector
            kind="audiooutput"
            label="Speakers"
            initialDeviceLabel={audioDevices.outputDeviceLabel}
            onDeviceChange={handleAudioOutputChange}
          />
          {outputSaved && (
            <View as="div" padding="none small">
              <Text size="legend" color="success">
                Device setting saved
              </Text>
            </View>
          )}
        </Card.Body>
      </Card>
    </>
  );
};

/**
 * RoomSettings Component
 * 
 * Provides settings interface for the VTC room, including:
 * - Audio device selection (microphone input)
 * - Audio output device selection
 * 
 * Can be used in drawer mode or modal mode.
 * 
 * @param {Object} props
 * @param {boolean} props.isModal - Whether component is rendered in a modal
 */
const RoomSettings = ({ isModal = false }) => {
  const drawerWidth = useAppStore((state) => state.drawerWidth);
  const room = useAppStore((state) => state.room);

  // If no room is available, show message
  if (!room) {
    return (
      <View
        as="div"
        maxWidth={isModal ? '100%' : drawerWidth}
        minWidth={isModal ? '100%' : drawerWidth}
        textAlign="center"
        margin="large auto"
        padding="large"
      >
        <Text>No active room connection. Please join a room first.</Text>
      </View>
    );
  }

  return (
    <View
      as="div"
      maxWidth={isModal ? '100%' : drawerWidth}
      minWidth={isModal ? '100%' : drawerWidth}
      padding="medium"
      height="100%"
      overflowY="auto"
    >
      <View as="div" margin="none none medium none">
        <Heading level="h3" margin="none none small none">
          Audio Settings
        </Heading>

      </View>

      <AudioDeviceSettings />

    </View>
  );
};

export default RoomSettings;
